import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { CroppedImageView } from '@bingads-webui-campaign-react/cropped-image-view';
import type { UAPPlacementComponentProps } from '../../@types';

import {
  MMAPlacementCard,
  PlacementHeadline,
  PlacementDescription,
  PlacementAction,
} from '../../shared-components';
import { withUAPLThemedStyles } from '../../with-themed-styles';
import { getBoostStartMenuStyles, getBoostActionStyles, getBoostBannerStyles } from '../../styles';
import vectorIcon from '../media/vector.svg';


const BoostTextAdPlacementBase = ({
  // UAPVMSingleImagePlacement
  placementData,

  isEnabled,
  showSuggested,
  showShortHeadline,
  shortHeadline,
  isMultiLineShortHeadline,
  showLongHeadline,
  longHeadline,
  isMultiLineLongHeadline,
  finalUrl,
  showDescription,
  description,
  isMultiLineDescription,
  showCallToAction,
  callToAction,
  showDismissAction,
  showLogo,
  logo,

  // may need to set window._TL_ in other platforms
  i18n,

  classes,
}: UAPPlacementComponentProps) => {
  if (!placementData || !placementData.placementMeta) {
    return null;
  }
  const { logoHeight } = placementData.placementMeta;

  return (
    <MMAPlacementCard url={finalUrl} isEnabled={isEnabled} mmaPlacementCardClassName={classes.placementCard}>
      { showLogo && logo &&
        <div className={classnames(classes.placementLogoContainer, 'logo-container')}>
          <CroppedImageView
            resizeImageHeight={logoHeight || 24}
            src={logo.Src}
            originalWidth={logo.Width}
            originalHeight={logo.Height}
            SourceX={logo.CropSettings.SourceX}
            SourceY={logo.CropSettings.SourceY}
            SourceWidth={logo.CropSettings.SourceWidth}
            SourceHeight={logo.CropSettings.SourceHeight}
            aspectRatio={1}
          />
        </div>
      }
      <div className={classnames(classes.placementTextContainer)}>
        {showSuggested && (
          <div className={classes.placementSuggestedContainer}>
            <img className={classes.placementVectorIcon} alt="" src={vectorIcon} />
            <div className={classes.placementSuggestedText}>{i18n.getString(_TL_('Suggested'))}</div>
          </div>
        )}
        <div className={classes.placementDetailsContainer}>
          {showShortHeadline && (
            <PlacementHeadline

              headlineText={shortHeadline}
              isMultiLine={isMultiLineShortHeadline}
              placementHeadlineClassName={classnames(classes.placementShortHeadline, 'short-headline')}
            />
          )}
          {showLongHeadline && (
            <PlacementHeadline

              headlineText={longHeadline}
              isMultiLine={isMultiLineLongHeadline}
              placementHeadlineClassName={classnames(classes.placementLongHeadline, 'long-headline')}
            />
          )}
          {showDescription && (
            <PlacementDescription

              description={description}
              isMultiLine={isMultiLineDescription}
              placementDescriptionClassName={classnames(classes.placementDescription, 'description')}
            />
          )}
        </div>
        <div className={classes.placementActionContainer}>
          {showCallToAction && callToAction && ( // add a space before each capital letter, e.g. "ActNow" => "Act Now"
            <PlacementAction actionText={callToAction.replace(/([A-Z])/g, ' $1')} placementActionClassName={classes.placementCallToAction} />
          )}
          {showCallToAction && callToAction && showDismissAction && (
            <PlacementAction actionText={i18n.getString(_TL_('No thanks'))} placementActionClassName={classes.placementDismissAction} />
          )}
        </div>
      </div>
    </MMAPlacementCard>
  );
};

BoostTextAdPlacementBase.propTypes = {
  placementData: PropTypes.shape({}).isRequired, // UAPVMSingleImagePlacement
  isEnabled: PropTypes.bool,

  showSuggested: PropTypes.bool,

  showShortHeadline: PropTypes.bool,
  shortHeadline: PropTypes.string,
  isMultiLineShortHeadline: PropTypes.bool,

  showLongHeadline: PropTypes.bool,
  longHeadline: PropTypes.string,
  isMultiLineLongHeadline: PropTypes.bool,

  finalUrl: PropTypes.string,

  showDescription: PropTypes.bool,
  description: PropTypes.string,
  isMultiLineDescription: PropTypes.bool,

  showDismissAction: PropTypes.bool,
  showCallToAction: PropTypes.bool,
  callToAction: PropTypes.string,

  showLogo: PropTypes.bool,
  logo: PropTypes.shape({
    Src: PropTypes.string,
    Width: PropTypes.number,
    Height: PropTypes.number,
    CropSettings: PropTypes.shape({
      SourceX: PropTypes.number,
      SourceY: PropTypes.number,
      SourceWidth: PropTypes.number,
      SourceHeight: PropTypes.number,
    }),
  }),

  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  classes: PropTypes.shape({}).isRequired,
};

BoostTextAdPlacementBase.defaultProps = {
  isEnabled: true,
  showSuggested: false,
  showShortHeadline: false,
  showLongHeadline: false,
  showDescription: false,
  showDismissAction: false,
  showCallToAction: false,
  showLogo: false,
  shortHeadline: false,
  isMultiLineShortHeadline: false,
  longHeadline: undefined,
  isMultiLineLongHeadline: false,
  finalUrl: undefined,
  description: undefined,
  isMultiLineDescription: false,
  callToAction: undefined,
  logo: undefined,
};

export const AdPlacementBoostStartMenu = withUAPLThemedStyles(

  (props: UAPPlacementComponentProps) => (<BoostTextAdPlacementBase
    {...props}
    showShortHeadline
    showLongHeadline
    showLogo
    isMultiLineShortHeadline
  />),
  getBoostStartMenuStyles,
  { componentName: 'AdPlacementBoostStartMenu' }
);
export const AdPlacementBoostActionCenter = withUAPLThemedStyles(

  (props: UAPPlacementComponentProps) => (<BoostTextAdPlacementBase
    {...props}
    showSuggested
    showShortHeadline
    showDescription
    showLogo
    isMultiLineShortHeadline
    showCallToAction
    showDismissAction
  />),
  getBoostActionStyles,
  { componentName: 'AdPlacementBoostActionCenter' }
);
export const AdPlacementBoostBanner = withUAPLThemedStyles(

  (props: UAPPlacementComponentProps) => (<BoostTextAdPlacementBase
    {...props}
    showLogo
    showShortHeadline
    isMultiLineShortHeadline
    showCallToAction
    showDismissAction
  />),
  getBoostBannerStyles,
  { componentName: 'AdPlacementBoostBanner' }
);
